// Pipsqueak collection page (PLP) enhancements:
//  - Quick-add: two steps. Tapping a size chip SELECTS that size; the tray's
//    "Add to basket" button then commits it to the cart via Horizon's native
//    CartAddEvent ('cart:update'). Selecting and adding were previously the
//    same tap, so brushing a size while browsing bought the item outright.
//  - Wishlist: heart toggles a saved-items list in localStorage (client-only).
import { CartAddEvent } from '@theme/events';

const WISH_KEY = 'pip_wishlist';

const getWish = () => {
  try { return JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); } catch { return []; }
};
const setWish = (list) => localStorage.setItem(WISH_KEY, JSON.stringify(list));

function markWishlist(root = document) {
  const list = getWish();
  root.querySelectorAll('.pip-pc__wish').forEach((h) => {
    const on = list.includes(h.dataset.productId);
    h.classList.toggle('is-on', on);
    h.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

// Step 1: selection. Single-select within one card's tray.
function selectSize(chip) {
  const tray = chip.closest('[data-pip-tray]');
  if (!tray) return;

  tray.querySelectorAll('.pip-pc__size').forEach((c) => {
    const on = c === chip;
    c.classList.toggle('is-selected', on);
    c.setAttribute('aria-pressed', on ? 'true' : 'false');
  });

  const addBtn = tray.querySelector('[data-pip-add]');
  if (addBtn) {
    addBtn.disabled = false;
    addBtn.setAttribute('aria-disabled', 'false');
    addBtn.dataset.variantId = chip.dataset.variantId;
    addBtn.classList.remove('is-added', 'is-error');
  }
}

// Step 2: commit. Only ever reached from the explicit button.
async function addSelected(btn) {
  if (btn.disabled || btn.classList.contains('is-loading')) return;
  const id = btn.dataset.variantId;
  if (!id) return;

  const tray = btn.closest('[data-pip-tray]');
  btn.classList.add('is-loading');
  try {
    const body = new FormData();
    body.append('id', id);
    body.append('quantity', '1');

    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
    const res = await fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    if (!res.ok) throw new Error('add failed');

    const cart = await (await fetch('/cart.js')).json();
    const card = btn.closest('[data-product-id]');
    document.dispatchEvent(
      new CartAddEvent(cart, (card && card.id) || 'pip-quick-add', {
        source: 'pip-quick-add',
        itemCount: cart.item_count,
        variantId: id,
      })
    );

    btn.classList.remove('is-loading');
    btn.classList.add('is-added');
    setTimeout(() => {
      btn.classList.remove('is-added');
      // Clear the selection so the next add is a deliberate choice again.
      if (tray) {
        tray.querySelectorAll('.pip-pc__size').forEach((c) => {
          c.classList.remove('is-selected');
          c.setAttribute('aria-pressed', 'false');
        });
      }
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      delete btn.dataset.variantId;
    }, 1300);
  } catch (e) {
    btn.classList.remove('is-loading');
    btn.classList.add('is-error');
    setTimeout(() => btn.classList.remove('is-error'), 1300);
  }
}

document.addEventListener('click', (e) => {
  const chip = e.target.closest('.pip-pc__size');
  if (chip && !chip.classList.contains('pip-pc__size--out')) {
    e.preventDefault();
    selectSize(chip);
    return;
  }
  const addBtn = e.target.closest('[data-pip-add]');
  if (addBtn) {
    e.preventDefault();
    addSelected(addBtn);
    return;
  }
  const heart = e.target.closest('.pip-pc__wish');
  if (heart) {
    e.preventDefault();
    const id = heart.dataset.productId;
    let list = getWish();
    list = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    setWish(list);
    markWishlist();
  }
});

// Initial paint + after filtering / infinite-scroll inject new cards.
markWishlist();
document.addEventListener('DOMContentLoaded', () => markWishlist());
document.addEventListener('filter:update', () => requestAnimationFrame(() => markWishlist()));

const grid = document.querySelector('.product-grid');
if (grid) {
  new MutationObserver(() => markWishlist(grid)).observe(grid, { childList: true, subtree: true });
}
